import { useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  isParentRole,
  isShirtSize,
  PARENT_ROLE_LABEL,
  PARENT_ROLES,
  ROLE_LABEL,
  SHIRT_SIZE_LABEL,
  SHIRT_SIZES,
} from "@domain/vocabulary";
import { PERSON_NAME_MAX_LENGTH } from "@domain/value-objects/PersonName";
import type { PersonId } from "@shared/brand";
import {
  Avatar,
  Banner,
  Button,
  Card,
  Chip,
  Field,
  Spinner,
  inputClass,
  selectClass,
} from "../../design-system";
import { formatDayWithYear, positionLabel } from "../../format";
import { useRuntimeConfigState } from "../../providers/RuntimeConfigProvider";
import { usePersonDetailViewModel } from "./usePersonDetailViewModel";
import { TerminationAction } from "./TerminationAction";
import { PermissionsAction } from "./PermissionsAction";
import { PersonDocumentsCard } from "./PersonDocumentsCard";
import { RoleChangeAction } from "./RoleChangeAction";
import { AssignVacationAction } from "./AssignVacationAction";
import { EmergencyContactsCard } from "./EmergencyContactsCard";
import { PersonalRecordCard } from "./PersonalRecordCard";
import { AssignedVacationsCard } from "./AssignedVacationsCard";
import { AreaTransferWizard } from "../Admin/AreaTransferWizard";
import { CompensationAction } from "./CompensationAction";
import { PositionPicker } from "../PositionPicker";
import styles from "./PersonDetailScreen.module.css";

/**
 * La ficha administrativa de una persona.
 *
 * **NO VIENE DEL PROTOTIPO.** Las veinte pantallas de `design/reference/screens/`
 * incluyen `perfil-del-empleado.html`, que es el perfil propio. No hay ninguna
 * ficha de *otra* persona, así que esta pantalla toma prestado el lenguaje del
 * perfil —las mismas tarjetas, el mismo avatar, los mismos grupos de datos, las
 * mismas franjas a todo el ancho— y no inventa ninguno nuevo.
 *
 * La diferencia con el perfil es de intención y se nota en una cosa: aquí los
 * campos se pueden corregir. Quien la abre no viene a consultar su saldo, viene
 * porque algo está mal en el padrón.
 */
export const PersonDetailScreen = (): ReactNode => {
  const params = useParams<{ personId: string }>();
  const navigate = useNavigate();
  const personId = (params.personId ?? "") as PersonId;
  const vm = usePersonDetailViewModel(personId);
  const { config, loaded: configLoaded } = useRuntimeConfigState();
  const [transferring, setTransferring] = useState(false);
  /* El puesto de quien se está mirando. `null` cuando no tiene ninguno
     registrado, y ahí esta pantalla se salta la línea. Ver `positionLabel`. */
  const puesto = positionLabel({
    positionId: vm.person.data?.positionId ?? null,
    positions: config.catalogs.positions,
    configLoaded,
  });

  if (vm.person.isPending) return <Spinner label="Cargando la ficha…" />;

  if (vm.person.isError || vm.person.data === undefined) {
    return (
      <Card>
        <Banner tone="warn">
          No pudimos cargar esta ficha. Puede que la persona no exista o que
          esté fuera de tu área.
        </Banner>
        <div className={styles.backRow}>
          <Button
            variant="ghost"
            onClick={() => {
              void navigate(-1);
            }}
          >
            ← Volver
          </Button>
        </div>
      </Card>
    );
  }

  const person = vm.person.data;
  const moved = vm.detailMutation.data?.periodMoved ?? null;

  /*
   * Lo que trajo el libro de eventos de RR.HH., de sólo lectura.
   *
   * «Sin registrar» y no ocultar la fila: son 20 fichas sin nacimiento y 64 sin
   * talla, y un hueco visible es lo que hace que alguien pregunte. Una fila que
   * desaparece no le pide nada a nadie — es la misma decisión que en el
   * expediente.
   */
  return (
    <div className={styles.screen}>
      {/*
        La salida va arriba y siempre visible. A esta pantalla se llega pulsando un
        avatar desde cualquier sitio y no tiene entrada en el menú, así que el menú
        no dice dónde estás — la misma situación que la búsqueda global, resuelta
        aquí con un paso atrás explícito en vez de con una entrada efímera, porque
        se vuelve a un lugar concreto y no a «lo suyo».
      */}
      <div className={styles.backRow}>
        <Button
          variant="ghost"
          onClick={() => {
            void navigate(-1);
          }}
        >
          ← Volver
        </Button>
      </div>

      <Card>
        {/* La acción de estado vive junto al estado. Ver `TerminationAction`. */}
        <div className={styles.identity}>
          <Avatar
            name={person.displayName}
            color="var(--color-brand)"
            size={72}
            photoURL={person.photoURL}
          />
          <div className={styles.identityText}>
            <h1 className={styles.name}>{person.displayName}</h1>
            {/*
              El puesto, que hasta ahora sólo se veía uno mismo.
              
              La ficha enseñaba rol, sub área y estado —lo que la persona puede
              hacer en el portal— y ninguna palabra sobre a qué se dedica. Quien la
              abre para arreglar algo del padrón necesita justo eso para saber si
              está en la ficha correcta: en producción 327 de 339 tienen puesto
              registrado, así que el dato estaba ahí y no se pintaba en ningún sitio
              salvo el perfil propio.

              **Y no se pinta hasta que la configuración llegue.** La ficha guarda
              el id y el título vive en el catálogo, que es otro documento: mientras
              viaja, el proveedor entrega los valores de fábrica —donde sólo existe
              «Sin asignar»— y pintar entonces enseñaba el identificador en crudo.
              Es exactamente el mismo cuidado que se toma el perfil propio, por el
              mismo fallo. Ver `positionTitle`.
            */}
            {puesto !== null && <p className={styles.position}>{puesto}</p>}
            {/* Sin correo es un caso normal —limpieza, mantenimiento— y se dice:
                un renglón vacío bajo el nombre se leía como que faltaba cargarlo. */}
            <p className={styles.email}>
              {person.email.length > 0 ? person.email : 'Sin correo · su líder le registra las vacaciones'}
            </p>
            <div className={styles.chips}>
              <Chip
                background="var(--color-brand-bg)"
                foreground="var(--color-brand)"
              >
                {ROLE_LABEL[person.role]}
              </Chip>
              {vm.subArea.data !== undefined && vm.subArea.data !== null ? (
                <Chip
                  background="var(--color-surface-subtle)"
                  foreground="var(--color-text-faint)"
                >
                  {vm.subArea.data.name}
                </Chip>
              ) : null}
              {/*
                Tres estados, no dos.

                Este chip era binario —«Activa» o «Pendiente de validación»— y con
                eso bastaba mientras `inactive` fuera algo que casi nadie ponía a
                mano. Con las bajas se alcanza de verdad, y el binario decía que
                quien se acababa de ir estaba **esperando que alguien la validara**:
                exactamente al revés, y sobre la ficha donde se comprueba.
              */}
              <Chip
                background={
                  person.status === "active"
                    ? "var(--color-success-bg)"
                    : person.status === "inactive"
                      ? "var(--color-danger-bg)"
                      : "var(--color-warning-bg)"
                }
                foreground={
                  person.status === "active"
                    ? "var(--color-success-ink)"
                    : person.status === "inactive"
                      ? "var(--color-danger-ink)"
                      : "var(--color-warning-ink)"
                }
              >
                {person.status === "active"
                  ? "Activa"
                  : person.status === "inactive"
                    ? "Dada de baja"
                    : "Pendiente de validación"}
              </Chip>
              {person.uid === null ? (
                <Chip
                  background="var(--color-surface-subtle)"
                  foreground="var(--color-text-faint)"
                >
                  Nunca ha entrado
                </Chip>
              ) : null}
            </div>
          </div>

          {/* La acción de estado vive junto al estado. Ver `TerminationAction`. */}
          <div className={styles.identityAction}>
            {/*
              El traslado sólo para quien está activo: mover de área a alguien dado
              de baja no significa nada, y el asistente cancelaría solicitudes de
              quien ya no puede tener ninguna.
            */}
            {vm.canEdit && person.status === 'active' && (
              <Button
                variant="secondary"
                onClick={() => {
                  setTransferring(true);
                }}
              >
                Trasladar de área
              </Button>
            )}
            {/*
              El rol se cambia sólo sobre quien está activo, por lo mismo: a quien
              se dio de baja se le revocaron las claims, y darle un rol nuevo sería
              apuntarlo en una ficha que nadie va a leer.
            */}
            {/*
              Los permisos van junto al rol porque son la misma pregunta a dos
              distancias: el rol dice el paquete y esto dice la excepción. Sólo
              sobre quien está activo, igual que el rol — a quien se dio de baja se
              le revocaron las claims.
            */}
            {person.status === 'active' && (
              <PermissionsAction
                person={person}
                canChange={vm.canGrantPermissions}
                onDone={async () => {
                  await vm.person.refetch();
                }}
              />
            )}
            {person.status === 'active' && (
              <RoleChangeAction
                person={person}
                canChange={vm.canChangeRole}
                onDone={async () => {
                  /* El rol mueve permisos y puede haber soltado sub áreas; la ficha
                     entera se relee para no quedarse enseñando lo de antes. */
                  await Promise.all([vm.person.refetch(), vm.leaders.refetch()]);
                }}
              />
            )}
            {/*
              Registrar vacaciones va con los otros dos: es una acción sobre esta
              persona, no una pantalla aparte. Sólo sobre quien está activo — a
              quien se dio de baja no se le agenda nada.
            */}
            <AssignVacationAction
              person={person}
              canAssign={vm.canChangeRole}
              onDone={async () => {
                /* El saldo se movió: la tarjeta de al lado lo enseña. */
                await vm.balance.refetch();
              }}
            />
            <TerminationAction
              person={person}
              canEdit={vm.canEdit}
              pendingVacationDays={vm.availableTotal}
              takenVacationDays={vm.balance.data?.takenDays ?? 0}
              entitledDays={vm.balance.data?.entitledDays ?? 0}
              onDone={async () => {
                await vm.person.refetch();
              }}
            />
          </div>
        </div>

        {transferring && (
          <AreaTransferWizard
            person={person}
            onClose={() => {
              setTransferring(false);
            }}
            onDone={async () => {
              /* La ficha entera: el traslado cambia área, sub área, líderes y
                 saldo, y refrescar sólo la persona dejaría el resto mintiendo. */
              await Promise.all([vm.person.refetch(), vm.leaders.refetch(), vm.balance.refetch()]);
            }}
          />
        )}

        {/*
          El pie de la tarjeta: quién le firma a la izquierda, y en la esquina el
          engrane de compensación. Van en la misma fila y no absolutamente
          posicionados, que sobre un texto de largo variable acabaría encima de él.
        */}
        <div className={styles.identityFoot}>
        {/*
          Cuatro estados, no dos: se sabe quiénes, tiene pero no se pudieron cargar
          los nombres, no se pudo consultar, y no tiene. Los dos de en medio se
          contaban como «no tiene» — la afirmación más alarmante de esta pantalla,
          dicha sin haber podido preguntar.
        */}
        {vm.leaders.data === undefined ? null : vm.leaders.data.people.length > 0 ? (
          <p className={styles.leaders}>
            Le firman: {vm.leaders.data.people.map((l) => l.displayName).join(" · ")}
          </p>
        ) : vm.leaders.data.linkCount > 0 ? (
          <p className={styles.leadersEmpty}>
            Tiene {vm.leaders.data.linkCount}{" "}
            {vm.leaders.data.linkCount === 1 ? "líder asignado" : "líderes asignados"}, pero
            no se pudieron cargar sus nombres. Sus solicitudes sí llegan.
          </p>
        ) : vm.leaders.data.failed ? (
          <p className={styles.leadersEmpty}>
            No pudimos consultar quién le firma. Vuelve a cargar la página; si sigue
            igual, es la conexión y no la ficha — no quiere decir que no tenga líder.
          </p>
        ) : (
          <p className={styles.leadersEmpty}>
            No tiene líderes asignados: sus solicitudes las firma quien administre
            su área.
          </p>
        )}
          <CompensationAction person={person} />
        </div>
      </Card>

      {/*
        La ficha y el saldo, lado a lado, y lo demás en franjas debajo.

        Eran dos columnas: datos, expediente y documentos apilados en una, y el
        saldo solo en la otra. Cada tarjeta que ganó la columna izquierda alargó
        el escalón —124 px, luego 323, luego 1041 con los documentos; 671 medidos
        el 7 oct 2026— y el hueco quedaba a media pantalla, al lado de lo que más
        se consulta. Es lo mismo que le pasaba al perfil propio y se arregla
        igual: no emparejar lo que crece con lo que no. Ficha y saldo miden
        parecido y comparten renglón; el resto baja a todo el ancho.
      */}
      <div className={styles.pair}>
        <Card title="Datos de la ficha">
          {!vm.canEdit ? (
            <Banner tone="info">
              Puedes consultar esta ficha. Corregirla necesita el permiso
              «Corregir datos de una persona».
            </Banner>
          ) : null}

          {/*
            Dos grupos con su rótulo, los mismos que la cabecera del perfil propio:
            lo de la empresa y lo personal. Eran siete campos en una sola columna
            —754 px de tarjeta, medido— y ahora van de dos en dos mientras quepan.
            El nombre y el correo se quedan con el renglón entero: son los largos.
          */}
          <h3 className={styles.groupLabel}>En la empresa</h3>
          <div className={styles.fieldGrid}>
            <div className={styles.fieldWide}>
              <Field label="Nombre completo" error={vm.nameError}>
                {(id) => (
                  <input
                    id={id}
                    className={inputClass(vm.nameError !== null)}
                    value={vm.current.displayName}
                    maxLength={PERSON_NAME_MAX_LENGTH}
                    disabled={!vm.canEdit}
                    onChange={(event) => {
                      vm.setDraft({ displayName: event.target.value });
                    }}
                  />
                )}
              </Field>
            </div>

            <div className={styles.fieldWide}>
              <Field
                label="Correo"
                hint={
                  person.uid === null
                    ? "Todavía no ha iniciado sesión: corregirlo aquí decide con qué cuenta entrará."
                    : "Ya tiene sesión. Al cambiarlo se mueve también su cuenta de Google, o no se cambia nada."
                }
              >
                {(id) => (
                  <input
                    id={id}
                    className={inputClass()}
                    type="email"
                    value={vm.current.email}
                    disabled={!vm.canEdit}
                    onChange={(event) => {
                      vm.setDraft({ email: event.target.value });
                    }}
                  />
                )}
              </Field>
            </div>

            {/*
              El puesto, del catálogo de RR.HH. y no en texto libre —el mismo
              buscador que el alta, `PositionPicker`—. Se ofrecen los encendidos,
              más el que ya tenga aunque lo hayan apagado: si no, la caja no
              podría mostrar su valor y parecería que no tiene. No se vacía: el
              alta lo exige, así que el hueco sólo aparece en las fichas que
              nunca tuvieron puesto, y no se puede volver a elegir.
            */}
            <Field label="Puesto">
              {(id) => (
                <PositionPicker
                  id={id}
                  positions={config.catalogs.positions}
                  value={vm.current.positionId}
                  disabled={!vm.canEdit}
                  onChange={(positionId) => {
                    vm.setDraft({ positionId });
                  }}
                  /* Quien corrige la ficha puede agregar el puesto que falte. */
                  canAdd={vm.canEdit}
                />
              )}
            </Field>

            <Field
              label="Fecha de ingreso"
              hint="Mueve su antigüedad, su derecho y el periodo al que se le cuentan los días."
            >
              {(id) => (
                <input
                  id={id}
                  className={inputClass()}
                  type="date"
                  value={vm.current.hireDate}
                  disabled={!vm.canEdit}
                  onChange={(event) => {
                    vm.setDraft({ hireDate: event.target.value });
                  }}
                />
              )}
            </Field>
          </div>

          <h3 className={styles.groupLabel}>Datos personales</h3>
          <div className={styles.fieldGrid}>
            {/*
              Nacimiento, talla y papá o mamá, editables desde el 28 sep 2026.

              Antes se veían y no se editaban aquí, y el motivo era bueno: no había
              callable detrás, y un campo editable sin ella es un formulario que
              guarda nada. Pero dejaba un circuito roto — la persona reportaba en
              «Confirma tus datos» que su fecha o su talla era otra, caía en «Datos
              reportados», y quien administra no tenía dónde ponerla. Ahora
              `updatePersonDetail` los acepta, con el mismo permiso que el resto de
              esta tarjeta.

              Siguen en la ficha y no en el expediente, que está más cerrado: quien
              felicita a su equipo suele ser justo quien no tiene `ver_expediente`.
            */}
            <Field label="Fecha de nacimiento">
              {(id) => (
                <input
                  id={id}
                  className={inputClass()}
                  type="date"
                  value={vm.current.birthDate}
                  disabled={!vm.canEdit}
                  onChange={(event) => {
                    vm.setDraft({ birthDate: event.target.value });
                  }}
                />
              )}
            </Field>

            <Field label="Talla de playera">
              {(id) => (
                <select
                  id={id}
                  className={selectClass()}
                  value={vm.current.shirtSize}
                  disabled={!vm.canEdit}
                  onChange={(event) => {
                    const valor = event.target.value;
                    vm.setDraft({ shirtSize: isShirtSize(valor) ? valor : "" });
                  }}
                >
                  <option value="">Sin registrar</option>
                  {SHIRT_SIZES.map((talla) => (
                    <option key={talla} value={talla}>
                      {SHIRT_SIZE_LABEL[talla]}
                    </option>
                  ))}
                </select>
              )}
            </Field>

            {/* En tercera persona: aquí se mira la ficha de alguien más. Ver el
                mismo renglón en el perfil, que sí tutea. La opción vacía junta dos
                cosas que la ficha guarda igual —no es su caso, y no contestó—, así
                que lo dice en vez de afirmar una. */}
            <Field label="¿Es papá o mamá?">
              {(id) => (
                <select
                  id={id}
                  className={selectClass()}
                  value={vm.current.parentRole}
                  disabled={!vm.canEdit}
                  onChange={(event) => {
                    const valor = event.target.value;
                    vm.setDraft({ parentRole: isParentRole(valor) ? valor : "" });
                  }}
                >
                  <option value="">Ninguno o sin responder</option>
                  {PARENT_ROLES.map((rol) => (
                    <option key={rol} value={rol}>
                      {PARENT_ROLE_LABEL[rol]}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          </div>

          {vm.detailMutation.isError ? (
            <Banner tone="warn">
              {String(vm.detailMutation.error.message)}
            </Banner>
          ) : null}

          {moved !== null ? (
            <Banner tone="warn">
              Cambió de periodo: pasó de {String(moved.previousPeriod ?? "—")} a{" "}
              {String(moved.period)}. Las solicitudes que ya había enviado se
              quedan contadas en el periodo anterior, así que estrena éste con
              su derecho completo. Si la corrección fue de pocos meses, revisa
              abajo los días ya gastados.
            </Banner>
          ) : null}

          {vm.canEdit ? (
            <div className={styles.actions}>
              <Button
                onClick={() => {
                  vm.detailMutation.mutate();
                }}
                disabled={
                  !vm.dirty ||
                  vm.nameError !== null ||
                  vm.detailMutation.isPending
                }
              >
                {vm.detailMutation.isPending ? "Guardando…" : "Guardar cambios"}
              </Button>
              <Button variant="ghost" onClick={vm.discard} disabled={!vm.dirty}>
                Descartar
              </Button>
            </div>
          ) : null}
        </Card>

        <Card
          className={styles.balanceCard}
          title={`Saldo del periodo ${String(vm.balance.data?.year ?? vm.periodYear)}`}
        >
          {/*
            Vacío no es cero, y aquí la diferencia asusta.

            El saldo se pide por periodo de aniversario, así que corregir la fecha
            de ingreso hace que la pantalla empiece a pedir uno nuevo. Sin registro
            se pintaban ceros —«Disponibles 0», sin renglones— y eso se lee como si
            al corregir un dato se le hubieran borrado los días a la persona. No se
            borran: el saldo del periodo anterior sigue guardado, intacto.
          */}
          {/*
            Un botón por periodo vigente, cuando hay más de uno.

            Con «Vencimiento del saldo» en más de 12 meses, lo que le queda del
            periodo anterior sigue valiendo junto al nuevo y se gasta primero. Cada
            uno tiene su propio «ya gastados fuera del portal», así que se ajustan
            por separado: elegir aquí cambia los números y el ajuste de abajo.
          */}
          {vm.periods.length > 1 ? (
            <div className={styles.periods} role="group" aria-label="Periodo a revisar">
              {vm.periods.map((period) => (
                <button
                  key={period.year}
                  type="button"
                  aria-pressed={period.selected}
                  className={`${styles.period} ${period.selected ? styles.periodActive : ""}`}
                  onClick={() => {
                    vm.selectPeriod(period.year);
                  }}
                >
                  {period.year}
                  <small>
                    {period.available} {period.available === 1 ? "día" : "días"} · hasta el{" "}
                    {formatDayWithYear(period.lastValidDay)}
                  </small>
                </button>
              ))}
            </div>
          ) : null}

          {vm.selectedLastValidDay !== null ? (
            <p className={styles.note}>
              Sus días valen hasta el {formatDayWithYear(vm.selectedLastValidDay)}.
            </p>
          ) : null}

          {vm.balanceMissing ? (
            <Banner tone="warn">
              Todavía no hay saldo registrado para el periodo {vm.periodYear}. No se
              perdió nada: el del periodo anterior sigue guardado, y éste se crea cuando
              el servidor recalcule los derechos. Mientras tanto, los números de abajo no
              son suyos.
            </Banner>
          ) : null}
          {/*
            No hay un campo «días disponibles» y esta nota explica por qué, porque es
            lo primero que alguien va a buscar aquí.
          */}
          <p className={styles.note}>
            Los días disponibles no se escriben: se calculan. Se corrigen los
            dos números de abajo, y el resultado se actualiza mientras escribes.
          </p>

          <ul className={styles.lines}>
            {vm.lines.map((line) => (
              <li key={line.label} className={styles.line}>
                <span className={styles.lineLabel}>
                  {line.label}
                  <small>{line.note}</small>
                </span>
                <span className={styles.lineValue}>{line.value}</span>
              </li>
            ))}
            <li className={`${styles.line} ${styles.lineTotal}`}>
              <span className={styles.lineLabel}>Disponibles</span>
              <span className={styles.lineValue}>{vm.availablePreview}</span>
            </li>
          </ul>

          {vm.availablePreview !== vm.availableSaved ? (
            <Banner tone="info">
              Con lo que hay escrito quedarían {vm.availablePreview} días, no{" "}
              {vm.availableSaved}. Todavía no se ha guardado.
            </Banner>
          ) : null}

          <div className={styles.fieldGrid}>
            <Field
              label={
                vm.carriedDebt > 0
                  ? "Descontados al abrir el periodo"
                  : "Días ya gastados fuera del portal"
              }
              hint={
                vm.carriedDebt > 0
                  ? `Incluye ${String(vm.carriedDebt)} de deuda del periodo anterior. El que corrige «se le contaron de más». No toca su derecho.`
                  : "El que corrige «se le contaron de más». No toca su derecho."
              }
            >
              {(id) => (
                <input
                  id={id}
                  className={inputClass()}
                  type="number"
                  min={0}
                  max={365}
                  value={vm.openingDraft ?? String(vm.openingSaved)}
                  disabled={!vm.canAdjustBalance}
                  onChange={(event) => {
                    vm.setOpeningDraft(event.target.value);
                  }}
                />
              )}
            </Field>

            {/* El derecho por contrato es de la persona, no de un periodo: se
                ofrece sólo sobre el nuevo para no sugerir que cambia uno viejo. */}
            {vm.isNewestPeriod ? (
            <Field
              label="Derecho por contrato"
              hint="Vacío = según la tabla de antigüedad. Se usa cuando el contrato da más días."
            >
              {(id) => (
                <input
                  id={id}
                  className={inputClass()}
                  type="number"
                  min={1}
                  max={90}
                  placeholder="Según antigüedad"
                  value={
                    vm.overrideDraft ??
                    (vm.overrideSaved === null ? "" : String(vm.overrideSaved))
                  }
                  disabled={!vm.canAdjustBalance}
                  onChange={(event) => {
                    vm.setOverrideDraft(event.target.value);
                  }}
                />
              )}
            </Field>
            ) : null}
          </div>

          {vm.balanceMutation.isError ? (
            <Banner tone="warn">
              {String(vm.balanceMutation.error.message)}
            </Banner>
          ) : null}

          {/* Sin `ajustar_saldo` los campos se ven pero no se tocan, y el botón no
              existe. Ver el saldo de alguien es parte de dirigirlo; cambiarlo no. */}
          {vm.canAdjustBalance ? (
            <div className={styles.actions}>
              <Button
                onClick={() => {
                  vm.balanceMutation.mutate();
                }}
                disabled={!vm.balanceDirty || vm.balanceMutation.isPending}
              >
                {vm.balanceMutation.isPending
                  ? "Ajustando…"
                  : "Ajustar el saldo"}
              </Button>
            </div>
          ) : null}

          <p className={styles.meta}>
            Ingresó el {vm.current.hireDate === "" ? "—" : vm.current.hireDate}.
          </p>
        </Card>
      </div>

      {/*
        Lo que quien mira registró a esta persona, con su botón de borrar.

        Va aquí y no en el tablero de aprobaciones por dos razones medidas: el
        superusuario **no tiene «Aprobaciones» en su menú** —su navegación es la
        de configurar la plataforma— y ese tablero exige el permiso `aprobar`,
        que RR.HH. no tiene aunque sí pueda registrar. Un botón allí habría
        quedado fuera del alcance de las dos personas que registran.

        La tarjeta se dibuja sola cuando hay algo: no ocupa sitio en las fichas
        a las que nadie ha registrado nada, que hoy son 332 de 335.
      */}
      <AssignedVacationsCard person={person} />

      {/*
        Lo que la persona contestó sobre sí misma y a quién llamar. Comparten
        renglón porque son la misma clase de dato; cada una tiene su permiso, y la
        que no se puede ver no existe — la otra se queda con el ancho entero.
      */}
      <div className={styles.pair}>
        <PersonalRecordCard person={person} />

        {/*
          A quién llamar si algo pasa. Va después del expediente y no dentro: lo
          gobierna otro permiso —`ver_sos`, que sí tiene un líder— y son datos de
          terceros, no de la persona. Sin ese permiso la tarjeta no existe.
        */}
        <EmergencyContactsCard person={person} />
      </div>

      {/*
        El expediente de contratación va junto al personal y no con las
        acciones de arriba: los dos son papeles de la persona y los gobierna el
        mismo par de permisos. La tarjeta entera desaparece sin `ver_expediente`.
      */}
      <PersonDocumentsCard
        person={person}
        onDone={async () => {
          await vm.person.refetch();
        }}
      />
    </div>
  );
};
